/**
 * FeatureItem Component
 * 
 * Reusable component for displaying individual feature items within the WhatsNew modal.
 * Handles rendering of feature icons, titles, descriptions with markdown support,
 * action links, and category chips with proper responsive design and accessibility.
 * 
 * Features:
 * - Feature icons/images with emoji fallback
 * - Rich text/markdown formatting for descriptions (basic support)
 * - Category display with styled Chip component
 * - Action links to relevant UI areas
 * - Proper spacing and responsive typography
 * - Accessibility: ARIA labels, semantic HTML, focus management
 * - Animations with prefers-reduced-motion support
 */

import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Avatar,
} from '@mui/material';
import { Feature } from '../types/featureConfig';

// Attempt to import ReactMarkdown if available
let ReactMarkdown: React.ComponentType<any> | null = null;
try {
  ReactMarkdown = require('react-markdown').default;
} catch {
  // ReactMarkdown not available, will fall back to plain text rendering
  ReactMarkdown = null;
}

interface FeatureItemProps {
  /** The feature to display */
  feature: Feature;
  /** Optional index for animation stagger */
  index?: number;
}

/**
 * FeatureItem component - renders individual feature information
 * 
 * @param feature - Feature object containing title, description, icon, link, category
 * @param index - Optional index for staggered animations
 */
const FeatureItem: React.FC<FeatureItemProps> = ({ feature, index = 0 }) => {
  // Map common icon names to emoji representations
  // This provides a simple icon system without requiring additional icon fonts
  const getIconEmoji = (iconName: string | undefined): string => {
    if (!iconName) return '✨';

    const iconMap: Record<string, string> = {
      lock: '🔒',
      megaphone: '📢',
      'chart-bar': '📊',
      'share-alt': '🔗',
      users: '👥',
      code: '💻',
      bell: '🔔',
      lightning: '⚡',
      star: '⭐',
      check: '✅',
      gear: '⚙️',
      rocket: '🚀',
    };

    return iconMap[iconName] || '✨';
  };

  return (
    <Box
      component="article"
      role="region"
      aria-label={`Feature: ${feature.title}`}
      sx={{
        mb: 3,
        p: { xs: 1.5, sm: 2 },
        borderRadius: '8px',
        backgroundColor: 'action.hover',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: `slideIn 0.5s ease-out ${index * 0.1}s backwards`,
        // Respect prefers-reduced-motion for users who prefer minimal animations
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          opacity: 1,
          transform: 'translateX(0)',
        },
        '&:hover': {
          backgroundColor: 'action.selected',
          transform: 'translateX(4px)',
          '@media (prefers-reduced-motion: reduce)': {
            transform: 'none',
          },
        },
        '@keyframes slideIn': {
          from: {
            opacity: 0,
            transform: 'translateX(-16px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
      }}
    >
      <Grid container spacing={2}>
        {/* Feature Icon */}
        {feature.icon && (
          <Grid item xs={2} sm={1}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Avatar
                aria-hidden="true"
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  backgroundColor: 'primary.light',
                  color: 'primary.main',
                  flexShrink: 0,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {getIconEmoji(feature.icon)}
              </Avatar>
            </Box>
          </Grid>
        )}

        {/* Feature Content */}
        <Grid item xs={feature.icon ? 10 : 12} sm={feature.icon ? 11 : 12}>
          {/* Title and Action Link */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'baseline' },
              gap: { xs: 0.5, sm: 1 },
              mb: 1,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              width: '100%',
            }}
          >
            <Typography
              component="h3"
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '0.95rem', sm: '1rem' },
                flex: { xs: '1 1 100%', sm: 'auto' },
                margin: 0,
              }}
            >
              {feature.title}
            </Typography>

            {/* Action Link Button */}
            {feature.link && (
              <Button
                component="a"
                href={feature.link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Learn more about ${feature.title} (opens in new tab)`}
                size="small"
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  textTransform: 'none',
                  ml: { xs: 0, sm: 'auto' },
                  py: 0.25,
                  px: 0.75,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                  },
                }}
                onClick={(e) => {
                  // Prevent event bubbling to modal
                  e.stopPropagation();
                }}
              >
                Learn more →
              </Button>
            )}
          </Box>

          {/* Description - with optional Markdown support */}
          <Box
            sx={{
              mt: 1,
              color: 'text.secondary',
              lineHeight: 1.6,
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              '& p': { margin: 0, marginBottom: '0.5rem' },
              '& ul, & ol': { margin: '0.5rem 0', paddingLeft: '1.5rem' },
              '& li': { marginBottom: '0.25rem' },
              '& strong, & b': { fontWeight: 600 },
              '& em, & i': { fontStyle: 'italic' },
              '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
            }}
          >
            {ReactMarkdown ? (
              <ReactMarkdown
                components={{
                  p: ({ children }: any) => <Typography variant="body2" component="p" sx={{ margin: 0, marginBottom: '0.5rem' }}>{children}</Typography>,
                  strong: ({ children }: any) => <strong>{children}</strong>,
                  em: ({ children }: any) => <em>{children}</em>,
                  ul: ({ children }: any) => <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ul>,
                  ol: ({ children }: any) => <ol style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ol>,
                  li: ({ children }: any) => <li style={{ marginBottom: '0.25rem' }}>{children}</li>,
                  a: ({ href, children }: any) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }} onClick={(e) => e.stopPropagation()}>
                      {children}
                    </a>
                  ),
                }}
              >
                {feature.description}
              </ReactMarkdown>
            ) : (
              <Typography variant="body2" component="div">
                {feature.description}
              </Typography>
            )}
          </Box>

          {/* Category Chip */}
          {feature.category && (
            <Box sx={{ mt: 1 }}>
              <Chip
                label={feature.category}
                size="small"
                variant="outlined"
                sx={{
                  height: 'auto',
                  '& .MuiChip-label': {
                    display: 'block',
                    whiteSpace: 'normal',
                  },
                }}
                aria-label={`Feature category: ${feature.category}`}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default FeatureItem;
